import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import Select from "react-select";
import { useQuery } from "react-query";
import ShipIcon from "../Media/livevessel.png";
import VoyageFlag from "../Media/voyageStartFlag.png"
import Waypoints from "../Media/waypoint.png"
import L from "leaflet";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  Polyline,
  useMap,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { RootState } from "index";
import {
  getVesselsAction,
  setOnboardedVessels,
  setVesselID,
  setVoyageID,
} from "Store/Generic/ReportingSlice";
import { queryKeyes } from "shared/queryKeys";
import { Get } from "VesselMaster/vesselMaster.hooks";
import Layout from "HorizontalMenu/Menu";
import Navbar from "HorizontalMenu/Navbar";
import { Container } from "reactstrap";
import { dateTimeFormat } from "GenericForms/Helper";

/* ------------------------------
   ENHANCED ICON URLS
-------------------------------- */
const ICONS = {
  port: "https://cdn-icons-png.flaticon.com/512/854/854878.png",          // port crane
  anchor: "https://cdn-icons-png.flaticon.com/512/1828/1828778.png",      // anchor
};

/* ------------------------------
   FIX DEFAULT LEAFLET ICON
-------------------------------- */
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

/* ------------------------------
   HELPER ICONS
-------------------------------- */
const waypointIcon = new L.Icon({
  iconUrl: Waypoints,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
  popupAnchor: [0, -12],
});

/* ------------------------------
   CONVERT DEG/MIN/SEC
-------------------------------- */
const convertToDecimal = (
  deg: number | null | undefined,
  min: number | null | undefined,
  sec: number | null | undefined,
  dir: string | null | undefined
) => {
  if (deg == null || min == null || sec == null || dir == null || dir === "") return null;
  let decimal = Number(deg) + Number(min) / 60 + Number(sec) / 3600;
  if (dir.toLowerCase() === "south" || dir.toLowerCase() === "west") decimal = decimal * -1;
  return Number(decimal.toFixed(6));
};

/* ------------------------------
   FIT BOUNDS
-------------------------------- */
interface FitBoundsProps {
  points: [number, number][];
}
const FitBounds: React.FC<FitBoundsProps> = ({ points }) => {
  const map = useMap();
  useEffect(() => {
    if (!points || points.length === 0) return;
    const bounds = L.latLngBounds(points);
    if (bounds.isValid()) map.fitBounds(bounds.pad(0.12));
  }, [points, map]);
  return null;
};

/* ------------------------------
   CREATE ROTATABLE SHIP ICON
-------------------------------- */
const createRotatedShipDivIcon = (headingDeg?: number, size = 56) => {
  const rotation = headingDeg ? headingDeg % 360 : 0;

  return L.divIcon({
    html: `
      <div style="
        width:${size}px;
        height:${size}px;
        transform:rotate(${rotation}deg);
        display:flex;
        align-items:center;
        justify-content:center;
      ">
        <img src="${ShipIcon}" style="width:${size * 0.75}px;height:${size * 0.75}px;" />
      </div>
    `,
    className: "",
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
};

L.Icon.Default.mergeOptions({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
});
/* ------------------------------
   ENHANCED CSS STYLES
-------------------------------- */
const styles = `
.vesselmap-control {
  position: absolute;
  top: 20px;
  left: 20px;
  z-index: 4000;
  width: 400px;
  padding: 20px;
  border-radius: 16px;
  background: linear-gradient(135deg, #0f2027 0%, #203a43 50%, #2c5364 100%);
  color: #ffffff;
  box-shadow: 0 15px 40px rgba(0,0,0,0.4);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255,255,255,0.1);
  font-family: 'Segoe UI', system-ui, -apple-system, sans-serif;
}

.vesselmap-control h5 {
  margin: 0 0 20px 0;
  font-size: 20px;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 12px;
  color: #00d4ff;
  text-shadow: 0 2px 8px rgba(0,212,255,0.3);
}

.vesselmap-control .label {
  font-weight: 600;
  font-size: 13px;
  color: #e0f7ff;
  margin-top: 16px;
  margin-bottom: 8px;
  display: block;
  letter-spacing: 0.3px;
}

.vesselmap-control .btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 10px;
  border: none;
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  transition: all 0.3s ease;
}

.btn-play {
  background: linear-gradient(135deg, #00b4db 0%, #0083b0 100%);
  color: white;
  box-shadow: 0 4px 15px rgba(0,180,219,0.4);
}

.btn-play:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0,180,219,0.6);
}

.btn-pause {
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
  color: white;
  box-shadow: 0 4px 15px rgba(245,87,108,0.4);
}

.btn-pause:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(245,87,108,0.6);
}

.btn-reset {
  background: rgba(255,255,255,0.1);
  color: #e0f7ff;
  border: 2px solid rgba(255,255,255,0.2);
}

.btn-reset:hover {
  background: rgba(255,255,255,0.2);
  border-color: rgba(255,255,255,0.4);
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none !important;
}

.controls-row {
  display: flex;
  gap: 10px;
  align-items: center;
  margin-top: 16px;
  flex-wrap: wrap;
}

.info-box {
  background: rgba(0,0,0,0.2);
  padding: 12px;
  border-radius: 10px;
  margin-top: 12px;
  border: 1px solid rgba(255,255,255,0.1);
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
  font-size: 13px;
}

.info-row:last-child {
  margin-bottom: 0;
}

.info-label {
  color: #a0d8ef;
  font-weight: 500;
}

.info-value {
  color: #ffffff;
  font-weight: 700;
}

.range {
  width: 100%;
  margin-top: 10px;
  height: 6px;
  border-radius: 3px;
  background: rgba(255,255,255,0.1);
  outline: none;
  -webkit-appearance: none;
}

.range::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #00d4ff;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0,212,255,0.5);
}

.range::-moz-range-thumb {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #00d4ff;
  cursor: pointer;
  border: none;
  box-shadow: 0 2px 8px rgba(0,212,255,0.5);
}

.legend {
  position: absolute;
  right: 20px;
  top: 20px;
  z-index: 3500;
  padding: 16px;
  border-radius: 12px;
  background: rgba(255,255,255,0.97);
  box-shadow: 0 8px 24px rgba(0,0,0,0.15);
  font-size: 13px;
  min-width: 200px;
}

.legend-title {
  font-weight: 700;
  font-size: 14px;
  margin-bottom: 12px;
  color: #1a1a1a;
  border-bottom: 2px solid #e0e0e0;
  padding-bottom: 8px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
  color: #333;
}

.legend-item:last-child {
  margin-bottom: 0;
}

.ship-divicon .ship-shadow {
  position: absolute;
  left: 10px;
  bottom: 4px;
  width: 65%;
  height: 8px;
  background: rgba(0,0,0,0.25);
  filter: blur(6px);
  border-radius: 50%;
  z-index: -1;
}

.pulse {
  animation: pulse 2s infinite;
  border-radius: 50%;
  opacity: 0.9;
}

@keyframes pulse {
  0% { box-shadow: 0 0 0 0 rgba(0,212,255,0.6); }
  70% { box-shadow: 0 0 0 16px rgba(0,212,255,0); }
  100% { box-shadow: 0 0 0 0 rgba(0,212,255,0); }
}

.start-marker, .end-marker {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: white;
  box-shadow: 0 4px 12px rgba(0,0,0,0.2);
}
`;

/* Custom Select Styles - Black text */
const customSelectStyles = {
  control: (base: any) => ({
    ...base,
    minHeight: 42,
    borderRadius: 10,
    border: '2px solid rgba(255,255,255,0.2)',
    background: 'rgba(255,255,255,0.95)',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    '&:hover': {
      borderColor: '#00d4ff',
    }
  }),
  menu: (base: any) => ({
    ...base,
    zIndex: 99999,
    borderRadius: 10,
    overflow: 'hidden',
    boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
  }),
  option: (base: any, state: any) => ({
    ...base,
    backgroundColor: state.isSelected ? '#00d4ff' : state.isFocused ? '#e6f7ff' : 'white',
    color: state.isSelected ? 'white' : '#1a1a1a',
    fontWeight: state.isSelected ? 600 : 400,
    padding: '12px 16px',
    cursor: 'pointer',
  }),
  singleValue: (base: any) => ({
    ...base,
    color: '#1a1a1a',
    fontWeight: 600,
  }),
  placeholder: (base: any) => ({
    ...base,
    color: '#666666',
  }),
  input: (base: any) => ({
    ...base,
    color: '#1a1a1a',
  }),
};

/* ------------------------------
   MAIN COMPONENT
-------------------------------- */
const VesselMap: React.FC = () => {
  const dispatch = useDispatch();
  const { Vessels, VesselID, VoyageID, onboardedVessels } = useSelector(
    (state: RootState) => state.Reporting
  );

  useEffect(() => {
    dispatch(getVesselsAction("vessel_master"));
  }, [dispatch]);

  useEffect(() => {
    if (Array.isArray(Vessels)) {
      const onboarded = Vessels.filter((v: any) => v.onboarding_status === "onboarded");
      dispatch(setOnboardedVessels(onboarded));
    }
  }, [Vessels, dispatch]);

  const vessel = (onboardedVessels || []).find((v: any) => v.id === VesselID) || null;

  const { data: voyages, isLoading: voyagesLoading } = useQuery(
    [queryKeyes.vessel.VoyageList.key, VesselID],
    async () => await Get(queryKeyes.vessel.VoyageList.url(VesselID), VesselID),
    { enabled: !!VesselID }
  );

  const { data: reports } = useQuery(
    [queryKeyes.vessel.vesselVoyageReporting.key, VesselID, VoyageID],
    async () =>
      await Get(
        queryKeyes.vessel.vesselVoyageReporting.url(VesselID, VoyageID),
        VesselID,
        VoyageID
      ),
    { enabled: !!VesselID && !!VoyageID, staleTime: Infinity }
  );

  const reportPoints = useMemo<[number, number][]>(() => {
    if (Array.isArray(reports) && reports.length > 0) {
      return reports
        .map((r: any) => {
          const lat = convertToDecimal(
            r.position_lat_deg,
            r.position_lat_min,
            r.position_lat_sec,
            r.lat_direction
          );
          const lon = convertToDecimal(
            r.position_long_deg,
            r.position_long_min,
            r.position_long_sec,
            r.long_direction
          );
          if (lat == null || lon == null) return null;
          return [lat, lon] as [number, number];
        })
        .filter(Boolean) as [number, number][];
    }

    return []; // ❗ No fallback
  }, [reports]);


  const [isPlaying, setIsPlaying] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [stepDelay, setStepDelay] = useState(1000);
  const [usePulsing, setUsePulsing] = useState(true);
  const intervalRef = useRef<number | null>(null);

  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (isPlaying && reportPoints.length > 0) {
      intervalRef.current = window.setInterval(() => {
        setCurrentIndex((prev) => {
          if (prev + 1 >= reportPoints.length) {
            setIsPlaying(false);
            return reportPoints.length - 1;
          }
          return prev + 1;
        });
      }, stepDelay);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isPlaying, stepDelay, reportPoints.length]);

  const currentPosition = reportPoints[currentIndex] || reportPoints[0] || null;

  const handleVesselSelection = (v: any) => {
    dispatch(setVesselID(v ? v.id : null));
    dispatch(setVoyageID(null));
  };
  const handleVoyageSelection = (v: any) => {
    dispatch(setVoyageID(v ? v.id : null));
  };

  const currentReport = Array.isArray(reports) && reports.length > 0 ? reports[currentIndex] : null;
  const currentHeading = currentReport ? Number(currentReport.heading || currentReport.course || currentReport.cog || currentReport.true_heading || 0) : undefined;
  const currentTimestamp = currentReport ? (currentReport.reported_at || currentReport.timestamp || currentReport.created_at) : undefined;

  const start = () => { if (reportPoints.length > 1) setIsPlaying(true); };
  const pause = () => setIsPlaying(false);
  const reset = () => { setIsPlaying(false); setCurrentIndex(0); };

  const polylineOptions = { color: "#00d4ff", weight: 5, opacity: 0.8 };

  const startMarkerIcon = new L.Icon({
    iconUrl: VoyageFlag,
    iconSize: [40, 40],
    iconAnchor: [20, 20],
    popupAnchor: [0, -20],
  });

  const endMarkerIcon = new L.Icon({
    iconUrl: VoyageFlag,
    iconSize: [40, 40],
    iconAnchor: [20, 20],
    popupAnchor: [0, -20],
  });

  const initialCenter: [number, number] = reportPoints && reportPoints.length > 0 ? reportPoints[0] : [20, 0];

  return (
    <React.Fragment>
      <Layout>
        <Navbar />
      </Layout>
      <div className="page-content">
        <Container fluid>
          <div style={{ position: "relative" }}>
            <style>{styles}</style>

            {/* CONTROL PANEL */}
            <div className="vesselmap-control ms-4 mt-0">
              <h5 className="d-flex align-items-center gap-2 mb-3">
                <img
                  src={ICONS.anchor}
                  width={24}
                  height={24}
                  alt="anchor"
                  className="icon-shadow"
                />
                Maritime Vessel Tracker
              </h5>

              {/* Vessel Selection */}
              <label className="label">Select Vessel</label>
              <Select
                options={onboardedVessels}
                getOptionValue={(o) => o.id}
                getOptionLabel={(o) => o.vessel_name}
                value={vessel}
                onChange={handleVesselSelection}
                isClearable
                styles={customSelectStyles}
                placeholder="Choose a vessel..."
              />

              {vessel && (
                <>
                  {/* Voyage Selection */}
                  <label className="label mt-3">Select Voyage</label>
                  <Select
                    options={
                      voyages?.filter(
                        (x: any) => x.display && [2, 3].includes(x.voyage_status)
                      ) || []
                    }
                    getOptionLabel={(o) => o.voyage_number}
                    getOptionValue={(o) => o.id}
                    onChange={handleVoyageSelection}
                    isLoading={voyagesLoading}
                    isClearable
                    styles={customSelectStyles}
                    placeholder="Choose a voyage..."
                  />

                  {/* Playback Controls */}
                  <div className="controls-row mt-3">
                    <button className="btn btn-play" onClick={start} disabled={isPlaying}>
                      ▶ Play
                    </button>

                    <button className="btn btn-pause" onClick={pause} disabled={!isPlaying}>
                      ❚❚ Pause
                    </button>

                    <button className="btn btn-reset" onClick={reset}>
                      ⟳ Reset
                    </button>
                  </div>

                  {/* Info Box */}
                  <div className="info-box mt-3">
                    <div className="info-row">
                      <span className="info-label">📍 Waypoint</span>
                      <span className="info-value">
                        {Math.min(currentIndex + 1, reportPoints.length)} / {reportPoints.length}
                      </span>
                    </div>

                    {currentTimestamp && (
                      <div className="info-row">
                        <span className="info-label">🕐 Timestamp</span>
                        <span className="info-value small-text">
                          {new Date(currentTimestamp).toLocaleString()}
                        </span>
                      </div>
                    )}

                    {currentHeading != null && (
                      <div className="info-row">
                        <span className="info-label">🧭 Heading</span>
                        <span className="info-value">{Math.round(currentHeading)}°</span>
                      </div>
                    )}
                  </div>

                  {/* Speed Slider */}
                  <label className="label mt-3">
                    Animation Speed:{" "}
                    <strong className="accent-text">{stepDelay}ms</strong>
                  </label>
                  <input
                    className="range"
                    type="range"
                    min={250}
                    max={5000}
                    step={250}
                    value={stepDelay}
                    onChange={(e) => setStepDelay(Number(e.target.value))}
                  />

                  {/* Pulse Toggle */}
                  <label className="toggle mt-3 d-flex align-items-center gap-2">
                    <input
                      type="checkbox"
                      checked={usePulsing}
                      onChange={(e) => setUsePulsing(e.target.checked)}
                    />
                    Enable Pulse Effect
                  </label>
                </>
              )}
            </div>

            {/* LEGEND */}
            <div className="legend">
              <div className="legend-title">Map Legend</div>
              <div className="legend-item">
                <img src={ShipIcon} width={24} height={24} alt="ship" />
                <span>Active Vessel</span>
              </div>
              <div className="legend-item">
                <img src={VoyageFlag} width={20} height={20} alt="start" />
                <span>Start Point</span>
              </div>
              <div className="legend-item">
                <img src={VoyageFlag} width={20} height={20} alt="end" />
                <span>End Point</span>
              </div>
              <div className="legend-item">
                <img src={Waypoints} width={18} height={18} alt="waypoint" />
                <span>Waypoints</span>
              </div>
              <div className="legend-item">
                <div style={{ width: 30, height: 4, background: "#00d4ff", borderRadius: 2, boxShadow: "0 0 8px rgba(0,212,255,0.5)" }} />
                <span>Route Path</span>
              </div>
            </div>

            {/* MAP */}
            <MapContainer center={initialCenter} zoom={3} minZoom={2} style={{ height: "580px", width: "100%" }}>
              {/* <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
               <TileLayer
          url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
          subdomains="abcd"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> &copy; <a href="https://carto.com/">CARTO</a>'
        /> */}
              {/* <TileLayer
  url="https://stamen-tiles.a.ssl.fastly.net/toner-lite/{z}/{x}/{y}.png"
  attribution='Map tiles: <a href="http://stamen.com/">Stamen Design</a> &copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>'
/> */}
              <TileLayer
                url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
                subdomains="abcd"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> &copy; <a href="https://carto.com/">CARTO</a>'
              />
              <FitBounds points={reportPoints as any} />
              <Polyline positions={reportPoints as any} pathOptions={polylineOptions} />
              {reportPoints.map((pos, i) => {
                const isCurrent = i === currentIndex;
                const isStart = i === 0;
                const isEnd = i === reportPoints.length - 1;
                if (isCurrent) return null;
                let iconToUse;
                if (isStart) iconToUse = startMarkerIcon;
                else if (isEnd) iconToUse = endMarkerIcon;
                else iconToUse = waypointIcon;
                return (
                  <Marker key={`pt-${i}`} position={pos} icon={iconToUse}>
                    <Popup>
                      <div style={{ minWidth: 180, padding: 4 }}>
                        <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 8, color: "#1a1a1a" }}>
                          📍 Waypoint #{i + 1}
                        </div>
                        {Array.isArray(reports) && reports[i] && reports[i].position_lat_deg && (
                          <div style={{ fontSize: 13, color: "#333", lineHeight: 1.6 }}>
                            <strong>Latitude:</strong> {reports[i].position_lat_deg}° {reports[i].position_lat_min}' {reports[i].position_lat_sec}" {reports[i].lat_direction}<br />
                            <strong>Longitude:</strong> {reports[i].position_long_deg}° {reports[i].position_long_min}' {reports[i].position_long_sec}" {reports[i].long_direction}<br />
                            <strong>Event Name:</strong> {reports[i].reporting_event_name}<br />
                            <strong>Date & Time</strong> {dateTimeFormat(reports[i].local_local_time, false, false, true)}<br />
                          </div>
                        )}
                      </div>
                    </Popup>
                  </Marker>
                );
              })}

              {currentPosition && (
                <Marker
                  position={currentPosition}
                  icon={createRotatedShipDivIcon(currentHeading, 60)}
                  zIndexOffset={700}
                >
                  <Popup>
                    <div style={{ minWidth: 180, padding: 4 }}>
                      <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 8, color: "#1a1a1a" }}>🚢 Current Position</div>
                      <div style={{ fontSize: 13, color: "#333", lineHeight: 1.8 }}>
                        <strong>Progress:</strong> {currentIndex + 1} / {reportPoints.length}<br />
                        {currentHeading != null && <><strong>Heading:</strong> {Math.round(currentHeading)}°<br /></>}
                        {currentTimestamp && <><strong>Time:</strong> {new Date(currentTimestamp).toLocaleTimeString()}</>}
                      </div>
                    </div>
                  </Popup>
                </Marker>
              )}

              {currentPosition && usePulsing && (
                <Marker
                  position={currentPosition}
                  icon={L.divIcon({
                    className: "pulse",
                    html: `<div style="width:26px;height:26px;border-radius:50%;background:rgba(0,212,255,0.85);"></div>`,
                    iconSize: [26, 26],
                    iconAnchor: [13, 13],
                  })}
                  interactive={false}
                />
              )}
            </MapContainer>
          </div>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default VesselMap;